import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Table, TableHead,
  TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Chip, Tooltip, Stack, InputAdornment, Switch,
  FormControlLabel, Divider, Collapse, Avatar, alpha,
} from '@mui/material';
import AddIcon            from '@mui/icons-material/Add';
import EditIcon           from '@mui/icons-material/Edit';
import DeleteIcon         from '@mui/icons-material/DeleteOutline';
import SearchIcon         from '@mui/icons-material/Search';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import LibraryBooksIcon   from '@mui/icons-material/LibraryBooks';
import RemoveRedEyeIcon   from '@mui/icons-material/RemoveRedEye';
import CloseIcon          from '@mui/icons-material/Close';
import BeachAccessIcon    from '@mui/icons-material/BeachAccess';
import HomeWorkIcon       from '@mui/icons-material/HomeWork';
import ComputerIcon       from '@mui/icons-material/Computer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon         from '@mui/icons-material/People';
import GavelIcon          from '@mui/icons-material/Gavel';
import AutoAwesomeIcon    from '@mui/icons-material/AutoAwesome';
import PageHeader from '../components/common/PageHeader';
import apiService from '../services/apiServices';
import useConfirm from '../hooks/useConfirm';

const CATS = ['Përgjithshme', 'HR', 'IT', 'Financiare', 'Operacional', 'Onboarding', 'Politika'];
const EMPTY = { title: '', content: '', category: 'Përgjithshme', tags: '', isPublished: true };

const TEMPLATES = [
  {
    icon: BeachAccessIcon,
    color: '#0ea5e9',
    bg: '#f0f9ff',
    label: 'Pushimet Vjetore',
    data: {
      title: 'Politika e Pushimeve Vjetore',
      category: 'HR',
      tags: 'pushime, HR, politika',
      isPublished: true,
      content: `POLITIKA E PUSHIMEVE VJETORE

1. E DREJTA PËR PUSHIM
Çdo punonjës me kohë të plotë ka të drejtë në 20 ditë pune pushim vjetor pas përfundimit të periudhës provuese (3 muaj). Punonjësit me kohë të pjesshme marrin pushim proporcional.

2. PLANIFIKIMI I PUSHIMIT
• Pushimet duhet të kërkohen të paktën 2 javë para datës së fillimit.
• Kërkesa bëhet përmes sistemit të HR ose direkt te menaxheri i drejtpërdrejtë.
• Miratimi varet nga nevojat operacionale të departamentit.

3. TRANSFERIMI I PUSHIMEVE
• Maksimumi 5 ditë pushim mund të transferohen në vitin e ardhshëm kalendarik.
• Ditët e pakonsumuara mbi këtë kufi humbasin automatikisht më 31 Dhjetor.

4. PUSHIMET GJATË PERIUDHËS PROVUESE
Punonjësit në periudhën provuese nuk kanë të drejtë pushimi, me përjashtim të rasteve urgjente të miratuara nga HR.

5. KONTAKTI
Për çdo pyetje: hr@kompania.com`,
    },
  },
  {
    icon: PeopleIcon,
    color: '#8b5cf6',
    bg: '#f5f3ff',
    label: 'Onboarding',
    data: {
      title: 'Udhëzuesi i Onboarding — Punonjësi i Ri',
      category: 'Onboarding',
      tags: 'onboarding, punonjës i ri, fillim pune',
      isPublished: true,
      content: `MIRË SE ERDHËT NË EKIP!

JAVA E PARË — ÇFARË DUHET TË BËNI

Dita 1:
✓ Merrni pajisjet dhe aksesin nga IT
✓ Plotësoni dokumentet e kontratës me HR
✓ Vizitoni zyrat dhe njihuni me ekipin
✓ Konfiguroni email-in dhe llogaritë e punës

Dita 2–3:
✓ Lexoni Kodin e Sjelljes dhe politikat e kompanisë
✓ Takohuni me menaxherin për objektivat e muajit të parë
✓ Fitoni akses në sistemet e brendshme (ERP, HR platform)

Java 2:
✓ Filloni trajnimin fillestar të departamentit
✓ Merrni pjesë në takimet e ekipit
✓ Caktoni mentoran tuaj (buddy)

KONTAKTET E RËNDËSISHME
• HR: hr@kompania.com
• IT Support: it@kompania.com
• Menaxheri juaj: shikoni organogramën

SISTEME QË DO TË PËRDORNI
• Nexuma — menaxhimi i prezencës dhe detyrave
• Email korporativ
• Slack / Teams — komunikimi i brendshëm`,
    },
  },
  {
    icon: HomeWorkIcon,
    color: '#10b981',
    bg: '#f0fdf4',
    label: 'Punë nga Shtëpia',
    data: {
      title: 'Politika e Punës nga Distanca (Remote Work)',
      category: 'Politika',
      tags: 'remote, punë nga shtëpia, hibrid',
      isPublished: true,
      content: `POLITIKA E PUNËS NGA DISTANCA

1. KRITERET E PRANUESHMËRISË
Punonjësit me mbi 6 muaj eksperiencë në kompani mund të aplikojnë për punë nga distanca, nëse roli i tyre e lejon këtë.

2. MODELI HIBRID (STANDARD)
• 3 ditë në zyrë / 2 ditë nga shtëpia në javë
• Ditët e zyrës caktohen në koordinim me menaxherin
• E marta dhe e enjta janë ditë zyrtare të pranisë (anchor days)

3. DETYRIMET GJATË PUNËS REMOTE
• Jini të arritshëm në oraret zyrtare (08:00–17:00)
• Regjistrojeni prezencën çdo ditë në sistem
• Kamera aktive gjatë takimeve video
• Siguroni lidhje stabile interneti dhe ambient pune profesional

4. PAJISJET DHE SIGURIA
Kompania ofron laptop. Punonjësi është përgjegjës për:
• Mbrojtjen fizike të pajisjeve
• Moslejimin e personave të paautorizuar të aksesojnë sistemet
• Përdorimin e VPN gjatë punës me të dhëna konfidenciale

5. ANULIMI I STATUSIT REMOTE
Menaxhmenti rezervon të drejtën të rishikojë dhe anulojë statusin remote nëse performance nuk plotëson standardet.`,
    },
  },
  {
    icon: ComputerIcon,
    color: '#f59e0b',
    bg: '#fffbeb',
    label: 'Mbështetja IT',
    data: {
      title: 'Si të Raportoni një Problem IT',
      category: 'IT',
      tags: 'IT, support, teknik, problem',
      isPublished: true,
      content: `PROCEDURA E RAPORTIMIT TË PROBLEMEVE IT

HAPAT E PARË — VETËNDIHMA
Përpara se të kontaktoni IT Support, provoni:
1. Rinisni pajisjen
2. Kontrolloni lidhjen e internetit
3. Çinstaloni dhe rinstaloni programin me problem
4. Kontrolloni nëse problemi prek vetëm ju ose gjithë ekipin

SI TË HAPNI NJË TICKET
Email: it@kompania.com
Subjekt: [KATEGORIA] Përshkrimi i shkurtër
Shembull: [LAPTOP] Ekrani nuk ndizet pas përditësimit

KATEGORITE E PRIORITETIT
🔴 URGJENT — Sistemi kryesor është jashtë funksionimit, afekton punën e gjithë ekipit. Kohë reagimi: 1 orë.
🟡 MESATAR — Problem personal i pajisjes ose softuerit. Kohë reagimi: 24 orë.
🟢 I ULËT — Kërkesë për pajisje ose softuer të ri. Kohë reagimi: 3–5 ditë pune.

INFORMACIONET QË DUHET TË JEPNI
• Lloji i pajisjes dhe sistemi operativ
• Mesazhi i gabimit (screenshot nëse mundeni)
• Kur filloi problemi dhe çfarë bëtë para se të ndodhë

ORARI I MBËSHTETJES
E Hënë – E Premte: 08:00–17:30
Jashtë orarit (vetëm URGJENT): +383 XX XXX XXX`,
    },
  },
  {
    icon: AccountBalanceWalletIcon,
    color: '#ef4444',
    bg: '#fff1f2',
    label: 'Shpenzimet',
    data: {
      title: 'Procedura e Rimbursimit të Shpenzimeve',
      category: 'Financiare',
      tags: 'shpenzime, rimbursim, financë, fatura',
      isPublished: true,
      content: `PROCEDURA E RIMBURSIMIT TË SHPENZIMEVE

SHPENZIMET E PRANUESHME
✓ Udhëtime pune (transport, akomodim, dietë)
✓ Materiale zyre të nevojshme
✓ Trajnime dhe konferenca profesionale të miratuara
✓ Dreka pune me klientë (me miratim paraprak)

KUFIZIME FINANCIARE
• Dietë ditore brenda vendit: deri €25/ditë
• Akomodim jashtë vendit: deri €120/natë
• Shpenzime mbi €200 kërkojnë miratim paraprak nga menaxheri

SI TË KËRKONI RIMBURSIM
1. Ruani të gjitha faturat origjinale
2. Plotësoni formularin e shpenzimeve në sistem (ose Excel)
3. Ngarkoni skanimin/foton e çdo fature
4. Dërgojini menaxherit për miratim brenda 10 ditëve pune

PAGESAT
Rimbursimi kryhet me pagën e muajit pasues për kërkesat e miratuara deri më 20 të muajit aktual.

SHPENZIMET E PAPRANUESHME
✗ Alkool (me përjashtim të drekave zyrtare të miratuara)
✗ Argëtim personal
✗ Gjoba trafiku
✗ Shpenzime familjare`,
    },
  },
  {
    icon: GavelIcon,
    color: '#6366f1',
    bg: '#eef2ff',
    label: 'Kodi i Sjelljes',
    data: {
      title: 'Kodi i Sjelljes dhe Etika Profesionale',
      category: 'Politika',
      tags: 'etika, sjellje, politika, kompani',
      isPublished: true,
      content: `KODI I SJELLJES DHE ETIKA PROFESIONALE

VLERAT TONA THEMELORE
• Integritet — veprojmë me ndershmëri në çdo situatë
• Respekt — trajtojmë të gjithë me dinjitet dhe konsideratë
• Bashkëpunim — e arrijmë suksesin bashkërisht
• Inovacion — nxisim ide dhe qasje të reja

MARRËDHËNIET NË VENDIN E PUNËS
Çdo punonjës ka të drejtë të punojë në një ambient pa diskriminim, ngacmim ose bullying. Raportimet bëhen konfidencialisht te HR.

KONFIDENCIALITETI
• Informacioni i klientëve dhe kompanisë trajtohet si konfidencial
• Ndalohet ndarja e të dhënave strategjike me të tretët
• Detyrimi i konfidencialitetit vazhdon edhe pas përfundimit të marrëdhënies së punës

KONFLIKTI I INTERESAVE
Njoftoni HR menjëherë nëse keni:
• Interesa financiare te konkurrentët
• Marrëdhënie familjare me furnitorë
• Aktivitete afariste anësore që ndikojnë punën

POLITIKA E MEDIAVE SOCIALE
• Mos postoni informacion konfidencial të kompanisë
• Mos flisni në emër të kompanisë pa autorizim
• Jini profesionalë kur identifikoheni si punonjës i kompanisë

SHKELJET
Shkeljet e Kodit të Sjelljes mund të rezultojnë në masa disiplinore deri në ndërprerje të punës.`,
    },
  },
];

// Accent i modulit — teal, që të dallohet nga Politikat (vjollcë) dhe
// Procedurat (qielli) brenda të njëjtit seksion "Rregullore & Procedura".
const ACCENT = '#0d9488';

const CAT_COLORS = {
  'Përgjithshme': { bg: '#F8FAFC', color: '#475569' },
  'HR':           { bg: '#EFF6FF', color: '#1D4ED8' },
  'IT':           { bg: '#F0FDF4', color: '#15803D' },
  'Financiare':   { bg: '#FEFCE8', color: '#A16207' },
  'Operacional':  { bg: '#ECFEFF', color: '#0E7490' },
  'Onboarding':   { bg: '#F5F3FF', color: '#6D28D9' },
  'Politika':     { bg: '#FDF4FF', color: '#7E22CE' },
};

const TH_SX = {
  fontWeight: 700, fontSize: '0.72rem', color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.25,
};

function CatChip({ label }) {
  const c = CAT_COLORS[label] || { bg: '#F1F5F9', color: '#334155' };
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: c.bg, color: c.color, height: 22,
        fontWeight: 700, fontSize: '0.7rem',
        border: `1px solid ${alpha(c.color, 0.18)}`,
      }}
    />
  );
}

function StatusBadge({ published }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{
        width: 7, height: 7, borderRadius: '50%',
        bgcolor: published ? '#22c55e' : '#94a3b8',
        boxShadow: published ? '0 0 0 2px #bbf7d0' : 'none',
      }} />
      <Typography variant="caption" sx={{ color: published ? '#15803d' : '#64748b', fontWeight: 600, fontSize: '0.72rem' }}>
        {published ? 'Publikuar' : 'Draft'}
      </Typography>
    </Stack>
  );
}

/** Metrikë inline për shiritin e veglave — pa kartela, pa lartësi të tepërt. */
function Metric({ value, label, color }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={0.6} sx={{ whiteSpace: 'nowrap' }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', lineHeight: 1, color: color || 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>{label}</Typography>
    </Stack>
  );
}

export default function KnowledgeBasePage() {
  const [rows,          setRows]          = useState([]);
  const [form,          setForm]          = useState(EMPTY);
  const [dlgOpen,       setDlgOpen]       = useState(false);
  const [viewDlg,       setViewDlg]       = useState(null);
  const [editing,       setEditing]       = useState(null);
  const [search,        setSearch]        = useState('');
  const [filterCat,     setFilterCat]     = useState('');
  const [filterPub,     setFilterPub]     = useState('');
  const [showTemplates, setShowTemplates] = useState(false);

  const load = useCallback(async () => {
    const r = await apiService().get('knowledgeBase');
    // Më i riu i pari — përndryshe artikulli i sapokrijuar humbet në fund të listës.
    const stamp = (x) => new Date(x.updatedAt || x.createdAt || 0).getTime() || 0;
    setRows([...(r?.data || [])].sort((a, b) => (stamp(b) - stamp(a)) || (b.id - a.id)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter(r => {
    const q  = search.toLowerCase();
    const mS = !search
      || r.title.toLowerCase().includes(q)
      || (r.tags || '').toLowerCase().includes(q)
      || (r.category || '').toLowerCase().includes(q);
    const mC = !filterCat || r.category === filterCat;
    const mP = !filterPub || (filterPub === 'published' ? !!r.isPublished : !r.isPublished);
    return mS && mC && mP;
  }), [rows, search, filterCat, filterPub]);

  const stats = useMemo(() => ({
    total:     rows.length,
    published: rows.filter(r => r.isPublished).length,
    views:     rows.reduce((s, r) => s + (r.views || 0), 0),
  }), [rows]);

  const isFiltered = !!(search || filterCat || filterPub);

  const openAdd  = () => { setForm({...EMPTY}); setEditing(null); setShowTemplates(true); setDlgOpen(true); };
  const openEdit = (r) => { setForm({...r}); setEditing(r.id); setShowTemplates(false); setDlgOpen(true); };

  const view = (r) => {
    const seen = { ...r, views: (r.views || 0) + 1 };
    setViewDlg(seen);
    setRows(prev => prev.map(x => (x.id === r.id ? seen : x)));
    // Ruajtja shkon në sfond — hapja e artikullit nuk pret rrjetin.
    apiService().put('knowledgeBase', seen).catch(() => {});
  };

  const save = async () => {
    if (editing) await apiService().put('knowledgeBase', { ...form, id: editing });
    else         await apiService().post('knowledgeBase', form);
    setDlgOpen(false); load();
  };

  const [confirm, confirmUI] = useConfirm();


  const del = async (id) => {
    if (!(await confirm({ title: 'Fshi artikullin', message: 'A jeni i sigurt që dëshironi ta fshini këtë artikull?', confirmLabel: 'Fshi' }))) return;
    try {
      await apiService().delete('knowledgeBase', id);
    } catch (e) {
      window.alert(`Artikulli nuk u fshi: ${e?.message || 'gabim i panjohur'}`);
    }
    load();
  };

  return (
    <Box>
      <PageHeader
        title="Qendra e Njohurive"
        subtitle="Artikuj, udhëzime dhe njohuri të brendshme të organizatës"
        actions={
          <Button
            variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none',
                  bgcolor: ACCENT, '&:hover': { bgcolor: alpha(ACCENT, 0.85), boxShadow: 'none' } }}
          >
            Artikull i Ri
          </Button>
        }
      />

      {/* Shiriti i veglave — filtrat majtas, metrikat djathtas (një rresht i vetëm) */}
      <Paper
        variant="outlined"
        sx={{ px: 1.5, py: 1, mt: 1.5, mb: 1.25, borderRadius: 2.5,
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          placeholder="Kërko artikull, kategori ose etiketë…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
            sx: { borderRadius: 1.5 },
          }}
          sx={{ width: 250 }}
        />
        <TextField select size="small" value={filterCat} onChange={e => setFilterCat(e.target.value)}
          InputProps={{ sx: { borderRadius: 1.5 } }} sx={{ width: 165 }}>
          <MenuItem value="">Të gjitha kategoritë</MenuItem>
          {CATS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField select size="small" value={filterPub} onChange={e => setFilterPub(e.target.value)}
          InputProps={{ sx: { borderRadius: 1.5 } }} sx={{ width: 140 }}>
          <MenuItem value="">Çdo gjendje</MenuItem>
          <MenuItem value="published">Publikuar</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
        </TextField>

        <Box flex={1} />

        <Stack
          direction="row" alignItems="center" spacing={1.25}
          divider={<Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />}
          sx={{ pr: 0.5 }}
        >
          <Metric value={stats.total}     label="artikuj"   />
          <Metric value={stats.published} label="publikuar" color="#15803d" />
          <Metric value={stats.views}     label="shikime"   color={ACCENT} />
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75, pl: 0.5 }}>
        {filtered.length} {filtered.length === 1 ? 'rezultat' : 'rezultate'}{isFiltered && ' • filtruar'}
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={TH_SX}>Titulli</TableCell>
              <TableCell sx={TH_SX}>Kategoria</TableCell>
              <TableCell sx={TH_SX}>Etiketa</TableCell>
              <TableCell sx={{ ...TH_SX, textAlign: 'center' }}>Shikime</TableCell>
              <TableCell sx={TH_SX}>Gjendja</TableCell>
              <TableCell width={104} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Avatar sx={{ bgcolor: '#F1F5F9', width: 48, height: 48 }}>
                      <LibraryBooksIcon sx={{ fontSize: 24, color: '#CBD5E1' }} />
                    </Avatar>
                    <Typography variant="body2" color="text.disabled" fontWeight={500}>
                      {isFiltered ? 'Asnjë artikull nuk përputhet me filtrat' : 'Ende nuk ka artikuj'}
                    </Typography>
                    {!isFiltered && (
                      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd}
                        sx={{ mt: 0.5, textTransform: 'none', borderRadius: 1.5, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
                        Shto artikullin e parë
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r, i) => {
              const tags = (r.tags || '').split(',').map(t => t.trim()).filter(Boolean);
              return (
                <TableRow
                  key={r.id}
                  hover
                  onClick={() => view(r)}
                  sx={{
                    cursor: 'pointer',
                    borderTop: i > 0 ? '1px solid #F1F5F9' : 'none',
                    '&:hover': { bgcolor: alpha(ACCENT, 0.03) },
                    transition: 'background 0.15s',
                  }}
                >
                  <TableCell sx={{ py: 1.1, maxWidth: 380 }}>
                    <Typography variant="body2" fontWeight={700} color="text.primary" noWrap>{r.title}</Typography>
                    {r.content && (
                      <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block', mt: 0.15 }}>
                        {r.content.slice(0, 90)}{r.content.length > 90 ? '…' : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><CatChip label={r.category} /></TableCell>
                  <TableCell sx={{ maxWidth: 190 }}>
                    {tags.length === 0
                      ? <Typography variant="caption" color="text.disabled">—</Typography>
                      : (
                        <Stack direction="row" spacing={0.4} alignItems="center">
                          {tags.slice(0, 2).map(t => (
                            <Chip key={t} label={t} size="small" variant="outlined"
                              sx={{ height: 20, fontSize: '0.68rem', color: '#475569', borderColor: '#E2E8F0' }} />
                          ))}
                          {tags.length > 2 && (
                            <Tooltip title={tags.slice(2).join(', ')}>
                              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>+{tags.length - 2}</Typography>
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.4} alignItems="center" justifyContent="center">
                      <RemoveRedEyeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">{r.views || 0}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell><StatusBadge published={!!r.isPublished} /></TableCell>
                  <TableCell align="right" sx={{ pr: 1.5 }}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <Tooltip title="Shiko"><IconButton size="small" onClick={e => { e.stopPropagation(); view(r); }}
                        sx={{ color: '#64748b', '&:hover': { bgcolor: alpha(ACCENT, 0.08), color: ACCENT } }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      <Tooltip title="Edito"><IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(r); }}
                        sx={{ color: '#64748b', '&:hover': { bgcolor: '#EFF6FF', color: '#2563eb' } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      <Tooltip title="Fshij"><IconButton size="small" onClick={e => { e.stopPropagation(); del(r.id); }}
                        sx={{ color: '#64748b', '&:hover': { bgcolor: '#FFF1F2', color: '#e11d48' } }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Shto / Edito artikull */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #F1F5F9', py: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, width: 36, height: 36, borderRadius: 1.5 }}>
              <LibraryBooksIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>
                {editing ? 'Edito Artikullin' : 'Artikull i Ri'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editing ? 'Përditëso përmbajtjen e artikullit ekzistues' : 'Nis nga një shabllon ose shkruaj nga e para'}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} mt={0.5}>

            {/* Shabllone — vetëm te artikulli i ri */}
            {!editing && (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <AutoAwesomeIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Shabllone të gatshme
                    </Typography>
                  </Stack>
                  <Button size="small" variant="text" onClick={() => setShowTemplates(v => !v)}
                    sx={{ fontSize: '0.72rem', minWidth: 0, textTransform: 'none', color: 'text.secondary' }}>
                    {showTemplates ? 'Fshih' : 'Shfaq'}
                  </Button>
                </Stack>
                <Collapse in={showTemplates}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    {TEMPLATES.map((t, i) => {
                      const TIcon = t.icon;
                      const selected = form.title === t.data.title;
                      return (
                        <Box
                          key={i}
                          onClick={() => setForm({ ...t.data })}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            px: 1, py: 0.6, borderRadius: 1.5, cursor: 'pointer',
                            border: '1px solid',
                            borderColor: selected ? t.color : '#E2E8F0',
                            bgcolor: selected ? t.bg : 'transparent',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: t.color, bgcolor: t.bg },
                          }}
                        >
                          <TIcon sx={{ fontSize: 15, color: t.color }} />
                          <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>{t.label}</Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Collapse>
                <Divider />
              </Box>
            )}

            <TextField
              size="small" label="Titulli *" value={form.title} fullWidth
              onChange={e => setForm(p => ({...p, title: e.target.value}))}
              placeholder="p.sh. Udhëzuesi i Onboarding — Punonjësi i Ri"
              InputProps={{ sx: { borderRadius: 1.5 } }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label="Kategoria" value={form.category}
                onChange={e => setForm(p => ({...p, category: e.target.value}))}
                sx={{ width: { xs: '100%', sm: 190 } }} InputProps={{ sx: { borderRadius: 1.5 } }}>
                {CATS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Etiketa (ndara me presje)" value={form.tags}
                onChange={e => setForm(p => ({...p, tags: e.target.value}))}
                placeholder="onboarding, politika, HR"
                sx={{ flex: 1 }} InputProps={{ sx: { borderRadius: 1.5 } }} />
              <FormControlLabel
                sx={{ ml: 0, whiteSpace: 'nowrap' }}
                control={<Switch size="small" checked={!!form.isPublished}
                  onChange={e => setForm(p => ({...p, isPublished: e.target.checked}))} />}
                label={<Typography variant="caption" fontWeight={600} color={form.isPublished ? 'success.dark' : 'text.secondary'}>
                  {form.isPublished ? 'Publikuar' : 'Draft'}
                </Typography>}
              />
            </Stack>

            <TextField
              size="small" label="Përmbajtja" multiline rows={11} fullWidth
              value={form.content}
              onChange={e => setForm(p => ({...p, content: e.target.value}))}
              placeholder="Shkruaj ose ngjit përmbajtjen e artikullit…"
              InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.875rem', lineHeight: 1.7 } }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid #F1F5F9', gap: 1 }}>
          <Button onClick={() => setDlgOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Anulo</Button>
          <Button variant="contained" onClick={save} disabled={!form.title}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, boxShadow: 'none',
                  bgcolor: ACCENT, '&:hover': { boxShadow: 'none', bgcolor: alpha(ACCENT, 0.85) } }}>
            {editing ? 'Ruaj ndryshimet' : 'Publiko artikullin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shiko artikullin */}
      <Dialog open={!!viewDlg} onClose={() => setViewDlg(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewDlg && (
          <>
            <DialogTitle sx={{ py: 1.5, pr: 1.5, borderBottom: '1px solid #F1F5F9' }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>{viewDlg.title}</Typography>
                  <Stack direction="row" spacing={1} mt={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <CatChip label={viewDlg.category} />
                    <StatusBadge published={!!viewDlg.isPublished} />
                    <Stack direction="row" spacing={0.4} alignItems="center">
                      <RemoveRedEyeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                      <Typography variant="caption" color="text.disabled" fontWeight={600}>{viewDlg.views || 0}</Typography>
                    </Stack>
                  </Stack>
                </Box>
                <IconButton size="small" onClick={() => setViewDlg(null)}><CloseIcon fontSize="small" /></IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {viewDlg.content
                ? <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>{viewDlg.content}</Typography>
                : <Typography variant="body2" color="text.disabled" fontStyle="italic">Ky artikull nuk ka përmbajtje.</Typography>}
              {viewDlg.tags && (
                <Box mt={2.5} pt={1.5} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {viewDlg.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                      <Chip key={t} label={t} size="small" variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', color: '#475569', borderColor: '#E2E8F0' }} />
                    ))}
                  </Stack>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid #F1F5F9', gap: 1 }}>
              <Button onClick={() => { openEdit(viewDlg); setViewDlg(null); }} startIcon={<EditIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}>Edito</Button>
              <Button variant="contained" onClick={() => setViewDlg(null)}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, boxShadow: 'none',
                      bgcolor: ACCENT, '&:hover': { boxShadow: 'none', bgcolor: alpha(ACCENT, 0.85) } }}>Mbyll</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {confirmUI}
    </Box>
  );
}
